/**
 * Gauge — dependency-free semicircular SVG gauge (v0.5.8 glassboard kit).
 *
 * Designed for bounded indices like Fear & Greed (0-100). Renders a banded
 * arc, a needle at the current value, and the numeric readout. Honest by
 * construction: a non-finite value renders nothing.
 */

export interface GaugeBand {
  /** Band applies up to (and including) this value. */
  upTo: number;
  /** Tailwind text-* token class used via currentColor. */
  toneClass: string;
  label?: string;
}

/** CNN-style Fear & Greed bands. */
export const FEAR_GREED_BANDS: GaugeBand[] = [
  { upTo: 25, toneClass: 'text-bad', label: 'extreme fear' },
  { upTo: 45, toneClass: 'text-warn', label: 'fear' },
  { upTo: 55, toneClass: 'text-ink-mute', label: 'neutral' },
  { upTo: 75, toneClass: 'text-accent', label: 'greed' },
  { upTo: 100, toneClass: 'text-ok', label: 'extreme greed' },
];

export interface GaugeProps {
  value: number;
  min?: number;
  max?: number;
  bands?: GaugeBand[];
  width?: number;
  /** Text under the number, e.g. the rating word. */
  caption?: string | null;
  className?: string;
}

function polar(cx: number, cy: number, r: number, deg: number) {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
}

/** Arc path from startDeg to endDeg (degrees, 180=left, 0=right). */
function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const s = polar(cx, cy, r, startDeg);
  const e = polar(cx, cy, r, endDeg);
  const largeArc = Math.abs(startDeg - endDeg) > 180 ? 1 : 0;
  return `M ${s.x.toFixed(2)} ${s.y.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${e.x.toFixed(2)} ${e.y.toFixed(2)}`;
}

export function Gauge({
  value,
  min = 0,
  max = 100,
  bands = FEAR_GREED_BANDS,
  width = 160,
  caption,
  className = '',
}: GaugeProps) {
  if (!Number.isFinite(value) || max <= min) return null;

  const w = width;
  const h = width * 0.62;
  const cx = w / 2;
  const cy = h - 8;
  const r = w / 2 - 8;
  const clamped = Math.min(max, Math.max(min, value));
  const frac = (clamped - min) / (max - min);
  // 180deg (left) -> 0deg (right)
  const needleDeg = 180 - frac * 180;

  const segments: { from: number; to: number; toneClass: string }[] = [];
  let prev = min;
  for (const b of bands) {
    const upTo = Math.min(max, b.upTo);
    if (upTo <= prev) continue;
    segments.push({
      from: 180 - ((prev - min) / (max - min)) * 180,
      to: 180 - ((upTo - min) / (max - min)) * 180,
      toneClass: b.toneClass,
    });
    prev = upTo;
  }

  const band = bands.find((b) => clamped <= b.upTo) ?? bands[bands.length - 1];
  const tip = polar(cx, cy, r - 6, needleDeg);

  return (
    <div className={`inline-flex flex-col items-center ${className}`.trim()} data-testid="gauge">
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} role="img" aria-label={`gauge ${Math.round(clamped)} of ${max}${band.label ? `, ${band.label}` : ''}`}>
        {segments.map((s, i) => (
          <path
            key={i}
            d={arcPath(cx, cy, r, s.from, s.to)}
            fill="none"
            stroke="currentColor"
            strokeWidth={6}
            strokeLinecap="butt"
            className={s.toneClass}
            opacity={0.85}
          />
        ))}
        <line x1={cx} y1={cy} x2={tip.x} y2={tip.y} stroke="currentColor" strokeWidth={2} className="text-ink" />
        <circle cx={cx} cy={cy} r={3} fill="currentColor" className="text-ink" />
      </svg>
      <div className={`num text-xl font-bold leading-none ${band.toneClass}`} data-testid="gauge-value">
        {Math.round(clamped)}
      </div>
      {caption != null && caption !== '' && (
        <div className="text-[11px] text-ink-mute capitalize">{caption}</div>
      )}
    </div>
  );
}
